import { makeMonitorRepository } from "../../core/repositories/repositoryBuilders.js";
import session from "../../core/session.js";

let instance;

/**
 * Service used to fetch calculated report data.
 *
 * ## Request throttling
 *
 * Calculation requests can be throttled, in which case requests are ignored
 * as long as new calculations are requested within a delay of one
 * second. After one second without any new request, the last calculation
 * request is sent to the server. Fox example, if 20 calculations are
 * requested with less than one second between each of them, only the 20th
 * request will be sent to the server.
 *
 * ## Aborting intermediate requests
 *
 * Each time a request is sent, any request waiting for a response is
 * aborted. This means this neither its success nor its error callback will
 * be evaluated.
 */
export default class ReportDataService {
	constructor() {
		this._throttlingDelay = 1000;
		this._throttle = null;

		this._repository = makeMonitorRepository({
			route: "/Shared/Report",
			antiForgeryToken: session.getAntiForgeryToken(),
		});
	}

	/**
	 * Return a promise resolving with calculated data.
	 *
	 * @param {boolean} [options.throttle] - If true, do request throttling.
	 * @param {report} [options.report] - Report for which data is requested.
	 * @param {object} [options.context] - Contextual information used for
	 * data calculation. It should always contain the company.
	 */
	fetchCalculatedReportData(options) {
		const awaitingCalculationFn = this._getCalculatedReportData.bind(
			this,
			options,
		);

		if (options.throttle) {
			return this._startThrottle(awaitingCalculationFn);
		}
		return awaitingCalculationFn();
	}

	_startThrottle(calculationFn) {
		if (this._throttle) {
			clearTimeout(this._throttle);
		}

		return new Promise((resolve, reject) => {
			this._throttle = setTimeout(() => {
				this._throttle = null;
				calculationFn().then(resolve, reject);
			}, this._throttlingDelay);
		});
	}

	_getCalculatedReportData({ report, context }) {
		const reportData = report.serialize();
		const dimensionSpecification = context.getDimensionSpecification();
		const data = {
			context: {
				companyId: context.getCompany().getLegacyId(),
				dimensionSpecification:
					dimensionSpecification &&
					dimensionSpecification.serialize(),
			},
			rows: reportData.rows,
			columns: reportData.columns,
		};

		return this._repository.save({ model: data, notify: false });
	}

	static instance() {
		instance ||= new this();

		return instance;
	}
}
